/**
 * useRowSync — edit rows on screen at once, save them behind the click.
 *
 * The rep-side counterpart of pages/portal/useItemSync.ts, generic over the row
 * shape so the inventory panel and the estimate lines share one implementation.
 *
 * ---------------------------------------------------------------------------
 * THE CONTRACT
 * ---------------------------------------------------------------------------
 *   - edit(key, patch) shows on screen immediately (view() merges it over the
 *     server row) and is sent ~600ms after the last edit to THAT row; several
 *     fields edited in the pause go as one PATCH.
 *   - ONE WRITE PER ROW IN FLIGHT. An edit made while a row is saving waits for
 *     that save and then goes, so two writes for one row never land out of order.
 *   - add() shows a new row at once under a temporary key, kept after its POST
 *     lands so the row does not remount under the cursor; remove() hides it at
 *     once and deletes behind.
 *   - A FAILED SAVE KEEPS THE EDIT ON SCREEN and marks the row 'error' with its
 *     message. retry(key) sends it again. Nothing is reverted silently.
 *   - When a batch has saved and nothing else is waiting, onSettled() runs ONCE
 *     — the caller's quiet refresh, for whatever the server computes from these
 *     rows (estimate totals, cached inventory totals). Overlay entries the
 *     refresh now covers are dropped after it, so the server is the source again.
 *
 * `busy` is true from the first edit until that refresh lands. A caller showing a
 * server-computed total uses it to mark the total as not final yet.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type RowStatus = 'idle' | 'pending' | 'saving' | 'error';

export interface RowMeta {
  /** Stable React key: the server id, or a temporary 'n…' key for a row added here. */
  _key: string;
  _status: RowStatus;
  _error: string | null;
}

interface Entry {
  key: string;
  id: string | null;
  /** For a row added here: what to create. */
  draft: Record<string, any> | null;
  /** Everything edited and not yet confirmed by a refresh — what view() shows. */
  shown: Record<string, any>;
  /** Edited and not yet sent. */
  unsent: Record<string, any>;
  deleted: boolean;
  /** Delete confirmed; hidden until a refresh no longer lists it. */
  gone: boolean;
  status: RowStatus;
  error: string | null;
  /** Bumped on every edit, so a refresh can tell which entries it saw. */
  version: number;
}

export interface RowSyncOps {
  update: (id: string, patch: Record<string, any>) => Promise<unknown>;
  remove?: (id: string) => Promise<unknown>;
  /** Must resolve to the created row's server id. */
  create?: (draft: Record<string, any>) => Promise<string>;
  /** After a batch saves: refresh whatever the server computes from these rows. */
  onSettled?: () => Promise<unknown>;
}

const DEBOUNCE_MS = 600;
const empty = (o: Record<string, any>) => Object.keys(o).length === 0;

export function useRowSync<T extends { id: string }>(ops: RowSyncOps) {
  const entries = useRef<Map<string, Entry>>(new Map());
  const byId = useRef<Map<string, string>>(new Map());          // server id -> entry key
  const timers = useRef<Record<string, number>>({});
  const inFlight = useRef<Map<string, Promise<void>>>(new Map());
  const opsRef = useRef(ops);
  opsRef.current = ops;
  const seq = useRef(0);
  const dirtySinceRefresh = useRef(false);
  const settling = useRef<Promise<void> | null>(null);
  const [tick, setTick] = useState(0);
  const bump = useCallback(() => setTick((t) => t + 1), []);

  const entryFor = (key: string): Entry => {
    let e = entries.current.get(key);
    if (!e) {
      e = { key, id: key, draft: null, shown: {}, unsent: {}, deleted: false, gone: false, status: 'idle', error: null, version: 0 };
      entries.current.set(key, e);
      byId.current.set(key, key);
    }
    return e;
  };

  const anyWaiting = () =>
    inFlight.current.size > 0
    || Object.keys(timers.current).length > 0
    || [...entries.current.values()].some((e) => e.status === 'pending' || e.status === 'saving');

  /** Once nothing is waiting: one refresh, then drop what it now covers. */
  const settle = () => {
    if (anyWaiting() || !dirtySinceRefresh.current || settling.current) return;
    dirtySinceRefresh.current = false;
    const seen = new Map([...entries.current.values()].map((e) => [e.key, e.version]));
    settling.current = (async () => {
      try {
        await opsRef.current.onSettled?.();
        for (const e of [...entries.current.values()]) {
          if (e.status !== 'idle' || seen.get(e.key) !== e.version || !empty(e.unsent)) continue;
          if (e.deleted && !e.gone) continue;
          entries.current.delete(e.key);
          if (e.id && byId.current.get(e.id) === e.key && !e.key.startsWith('n')) byId.current.delete(e.id);
          // A row added here keeps its temporary key for good — dropping the
          // mapping would remount it under the cursor. Its edits are confirmed.
          if (e.key.startsWith('n') && !e.gone) {
            entries.current.set(e.key, { ...e, draft: null, shown: {}, unsent: {} });
          }
        }
      } catch {
        // The saves landed; only the refresh failed. Keep the overlay — it is
        // what the server holds — and try again after the next edit.
        dirtySinceRefresh.current = true;
      } finally {
        settling.current = null;
        bump();
      }
    })();
  };

  const flushRef = useRef<(key: string) => Promise<void>>(async () => {});
  flushRef.current = (key: string): Promise<void> => {
    window.clearTimeout(timers.current[key]);
    delete timers.current[key];
    const running = inFlight.current.get(key);
    if (running) return running;
    const e = entries.current.get(key);
    if (!e) return Promise.resolve();

    const o = opsRef.current;
    let job: (() => Promise<void>) | null = null;

    if (e.deleted && !e.gone) {
      if (!e.id) { entries.current.delete(key); bump(); return Promise.resolve(); }
      job = async () => { await o.remove!(e.id!); e.gone = true; };
    } else if (!e.deleted && !e.id && e.draft) {
      const body = { ...e.draft, ...e.unsent };
      e.unsent = {};
      job = async () => {
        try {
          const id = await o.create!(body);
          e.id = id;
          byId.current.set(id, key);
        } catch (err) { e.unsent = { ...body, ...e.unsent }; throw err; }
      };
    } else if (!e.deleted && e.id && !empty(e.unsent)) {
      const body = e.unsent;
      e.unsent = {};
      job = async () => {
        try { await o.update(e.id!, body); } catch (err) { e.unsent = { ...body, ...e.unsent }; throw err; }
      };
    }
    if (!job) {
      if (e.status === 'pending') e.status = 'idle';
      bump(); settle();
      return Promise.resolve();
    }

    e.status = 'saving';
    e.error = null;
    dirtySinceRefresh.current = true;
    bump();
    const p = job()
      .then(() => { e.status = 'idle'; })
      .catch((err: any) => { e.status = 'error'; e.error = err?.message || 'Not saved'; })
      .then(async () => {
        inFlight.current.delete(key);
        const more = e.status !== 'error' && ((e.deleted && !e.gone) || (!e.deleted && !empty(e.unsent)));
        if (more) { await flushRef.current(key); return; }
        bump();
        settle();
      });
    inFlight.current.set(key, p);
    return p;
  };

  const schedule = (key: string, ms = DEBOUNCE_MS) => {
    window.clearTimeout(timers.current[key]);
    timers.current[key] = window.setTimeout(() => { void flushRef.current(key); }, ms);
  };

  const mark = (e: Entry) => {
    e.version++;
    if (!inFlight.current.has(e.key)) { e.status = 'pending'; e.error = null; }
  };

  /** Edit fields of a row (by its _key). */
  const edit = useCallback((key: string, patch: Record<string, any>) => {
    const e = entryFor(key);
    e.shown = { ...e.shown, ...patch };
    e.unsent = { ...e.unsent, ...patch };
    mark(e);
    bump();
    schedule(key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  /** A new row, on screen now. `draft` is what create() receives. */
  const add = useCallback((draft: Record<string, any>) => {
    const key = `n${++seq.current}`;
    const e: Entry = { key, id: null, draft, shown: { ...draft }, unsent: {}, deleted: false, gone: false, status: 'pending', error: null, version: 1 };
    entries.current.set(key, e);
    bump();
    schedule(key);
    return key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  /** Hide a row now and delete it behind. Not batched: there is nothing to coalesce. */
  const remove = useCallback((key: string) => {
    const e = entryFor(key);
    e.deleted = true;
    e.unsent = {};
    mark(e);
    bump();
    schedule(key, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bump]);

  const retry = useCallback((key: string) => {
    const e = entries.current.get(key);
    if (e && e.status === 'error') { e.status = 'pending'; e.error = null; bump(); void flushRef.current(key); }
  }, [bump]);

  const flushAll = useCallback(async () => {
    await Promise.all([...entries.current.keys()].map((k) => flushRef.current(k)));
    return ![...entries.current.values()].some((e) => e.status === 'error');
  }, []);

  /**
   * Server rows with every unconfirmed edit laid over them, deleted rows hidden
   * (unless the delete failed — then shown, with its error), rows added here
   * included until the server lists them.
   */
  const view = useCallback((rows: T[]): (T & RowMeta)[] => {
    void tick;
    const out: (T & RowMeta)[] = [];
    const listed = new Set<string>();
    for (const r of rows) {
      const key = byId.current.get(r.id) ?? r.id;
      const e = entries.current.get(key);
      listed.add(key);
      if (e && e.deleted && e.status !== 'error') continue;
      out.push({ ...r, ...(e?.shown ?? {}), id: r.id, _key: key, _status: e?.status ?? 'idle', _error: e?.error ?? null });
    }
    for (const e of entries.current.values()) {
      if (listed.has(e.key) || !e.key.startsWith('n') || e.gone) continue;
      if (e.deleted && e.status !== 'error') continue;
      if (!e.draft && empty(e.shown)) continue;
      out.push({ ...(e.draft ?? {}), ...e.shown, id: e.id ?? e.key, _key: e.key, _status: e.status, _error: e.error } as T & RowMeta);
    }
    return out;
  }, [tick]);

  // Last chance to send when the tab is hidden or closed.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') void flushAll(); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      void flushAll();   // the panel closing is the same moment
    };
  }, [flushAll]);

  const all = [...entries.current.values()];
  const failed = all.filter((e) => e.status === 'error').length;
  const busy = all.some((e) => e.status === 'pending' || e.status === 'saving')
    || dirtySinceRefresh.current || settling.current !== null;

  return { view, edit, add, remove, retry, flushAll, busy, failed };
}
